import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, Check, ArrowRight, Gift, Moon } from "lucide-react";
import Seo from "@/components/Seo";
import DictateButton from "@/components/DictateButton";
import FestiveCountdown, { LAST_ORDER_LABEL } from "@/components/FestiveCountdown";

const POSTER = "/diwali-poster.png";
const MACARON_POSTER = "/motichoor-macarons.jpg";
const KARWA_POSTER = "/karwa-chauth.jpg";

const BOX_PRICE = 1200;
const HAMPER_TIERS = [
  { boxes: 1, label: "Single box", note: "6 macarons · one keepsake box" },
  { boxes: 6, label: "Family set", note: "6 boxes · 36 macarons" },
  { boxes: 12, label: "Gifting dozen", note: "12 boxes for the whole list" },
  { boxes: 25, label: "Corporate hamper", note: "25+ boxes · special rate on request", corporate: true },
];

const PRODUCTS = {
  motichoor_macarons: {
    label: "Motichoor Macarons",
    cta: "Reserve gift boxes",
    quantityLabel: "How many boxes?",
    quantityPlaceholder: "e.g. 12 boxes",
    dateLabel: "Delivery date",
  },  karwa_chauth: {
    label: "Karwa Chauth Dinner",
    cta: "Book my chef",
    quantityLabel: "",
    dateLabel: "Dinner date",
  },
  diwali_catering: {
    label: "Diwali Catering",
    cta: "Book the celebration",
    quantityLabel: "",
    dateLabel: "Event date",
  },
};

export default function Diwali() {
  const location = useLocation();
  const [product, setProduct] = useState("motichoor_macarons");
  const [form, setForm] = useState({ name: "", phone: "", city: "Mumbai", date: "", quantity: "", guests: "", message: "" });
  const [sending, setSending] = useState(false);

  const cfg = PRODUCTS[product];

  useEffect(() => {
    if (location.hash !== "#enquire") return;
    const t = setTimeout(() => document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth" }), 250);
    return () => clearTimeout(t);
  }, [location]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.phone.replace(/\D/g, "").length < 10)
      return toast.error("Please share your name and a valid phone number");
    setSending(true);
    try {
      const qty = parseInt(form.quantity, 10) || 0;
      const payload = { product, ...form };
      if (product === "motichoor_macarons" && qty > 0) {        payload.quantity = `${qty} box${qty > 1 ? "es" : ""} (${qty * 6} macarons)`;
        payload.message = [
          qty >= 25 ? "Corporate hamper — rate on request" : `Indicative total ₹${(qty * BOX_PRICE).toLocaleString("en-IN")} at ₹${BOX_PRICE}/box`,
          form.message,
        ].filter(Boolean).join(" · ");
      }
      await api.post("/leads/festive", payload);
      toast.success("Request received — our concierge will call you shortly.");
      setForm({ name: "", phone: "", city: "Mumbai", date: "", quantity: "", guests: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="diwali-page">
      <Seo
        title="Diwali & Karwa Chauth — Motichoor Macarons, Private Chefs & Festive Feasts | Chef Hire"
        description="Special-edition Motichoor Macaron gift boxes, a private chef for Karwa Chauth, and the Fusional Feast Diwali sit-down from ₹1899 per person. 100% vegetarian, saffron, pistachio & gold. Mumbai, Bengaluru, Indore, Delhi NCR & Hyderabad."
        path="/diwali"
        keywords="Diwali gift box, Motichoor macarons, Karwa Chauth private chef, Diwali party catering, Fusional Feast, festive catering India"
      />

      {/* ---------- Festive hero products ---------- */}
      <section className="relative overflow-hidden grain border-b border-white/5">
        <div className="absolute inset-0">
          <img src={MACARON_POSTER} alt="" aria-hidden className="w-full h-full object-cover opacity-15 blur-md scale-110" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/90 via-[#0A0A0A]/90 to-[#0A0A0A]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 md:pt-24">
          <span className="chip text-[#F5B041] border-[#F5B041]/40"><Sparkles size={12} /> Festive specials · Limited edition</span>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mt-6 max-w-3xl">
            Diwali gifting,<br />
            <span className="font-script text-[#F5B041] text-6xl sm:text-7xl lg:text-8xl">for your loved ones.</span>
          </h1>
          <p className="mt-6 text-lg text-neutral-300 max-w-2xl leading-relaxed">
            Two festive pieces from our kitchen — a special-edition Motichoor Macaron gift box, and a private
            chef who cooks Karwa Chauth dinner at your home.
          </p>

          <div className="grid md:grid-cols-2 gap-8 mt-14">
            {/* Macaron gift box */}
            <article className="border border-[#F5B041]/20 bg-black/40 backdrop-blur group" data-testid="product-macarons">
              <div className="overflow-hidden">
                <img src={MACARON_POSTER} alt="Motichoor Macarons special-edition Diwali gift box by Chef Hire"
                  data-testid="macaron-poster"
                  className="w-full aspect-[4/5] object-cover transition duration-700 group-hover:scale-[1.03]" />
              </div>
              <div className="p-7">
                <div className="flex items-center gap-2">
                  <Gift size={14} className="text-[#F5B041]" />
                  <span className="small-caps text-[10px] text-neutral-500">Special edition · 100% vegetarian</span>
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl mt-3">Motichoor Macarons</h2>
                <p className="font-script text-xl text-[#F5B041] mt-1">a Diwali gift box</p>
                <p className="text-neutral-400 text-sm mt-4 leading-relaxed">
                  A luxury fusion of the motichoor laddoo and the French macaron — saffron, pistachio and
                  edible gold, boxed in an embossed keepsake drawer with a ribboned tag. Made to order for
                  family, clients and festive hampers.
                </p>
                <ul className="mt-5 space-y-2">
                  {["Saffron, pistachio & edible 24k gold", "Six macarons per keepsake box, hand-piped to order",
                    "Embossed gift box with ribbon tag", "Corporate hampers & bulk orders welcome"].map((t) => (
                    <li key={t} className="flex items-start gap-2 text-xs text-neutral-300">
                      <Check size={13} className="text-[#F5B041] mt-0.5 shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
                <div className="dotted-sep my-6" />
                <FestiveCountdown testId="macaron-countdown" />
                <div className="flex items-end justify-between gap-4 mt-5">
                  <div>
                    <div className="small-caps text-[10px] text-neutral-500">Per box of 6</div>
                    <div className="font-serif-display text-3xl text-[#F5B041]">₹{BOX_PRICE.toLocaleString("en-IN")}</div>
                    <div className="small-caps text-[10px] text-neutral-500">Corporate rate above 25 boxes</div>
                  </div>
                  <button onClick={() => { setProduct("motichoor_macarons"); document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth" }); }}
                    data-testid="cta-macarons"
                    className="btn-primary small-caps text-[10px] inline-flex items-center gap-2">
                    Book for Diwali <ArrowRight size={13} />
                  </button>
                </div>
              </div>
            </article>

            {/* Karwa Chauth */}
            <article className="border border-[#F5B041]/20 bg-black/40 backdrop-blur group" data-testid="product-karwa-chauth">
              <div className="overflow-hidden">
                <img src={KARWA_POSTER} alt="Book a private chef for Karwa Chauth — exotic festive feasts at your home"
                  data-testid="karwa-poster"
                  className="w-full aspect-[4/5] object-cover transition duration-700 group-hover:scale-[1.03]" />
              </div>
              <div className="p-7">
                <div className="flex items-center gap-2">
                  <Moon size={14} className="text-[#F5B041]" />
                  <span className="small-caps text-[10px] text-neutral-500">Moonrise dinner · 100% vegetarian</span>
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl mt-3">A Chef for Karwa Chauth</h2>
                <p className="font-script text-xl text-[#F5B041] mt-1">treat her to a festive feast</p>
                <p className="text-neutral-400 text-sm mt-4 leading-relaxed">
                  Sargi at dawn and a candlelit dinner the moment the moon shows — our chef cooks it all in
                  your kitchen, lays the table, serves course by course and leaves it spotless.
                </p>
                <ul className="mt-5 space-y-2">
                  {["Sargi thali in the morning, dinner after moonrise", "Pure vegetarian, satvik and fasting-friendly",
                    "Festive table styling with diyas and flowers", "Chef, service team, setup and cleanup included"].map((t) => (
                    <li key={t} className="flex items-start gap-2 text-xs text-neutral-300">
                      <Check size={13} className="text-[#F5B041] mt-0.5 shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
                <div className="dotted-sep my-6" />
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <div className="small-caps text-[10px] text-neutral-500">Per evening</div>
                    <div className="font-serif-display text-2xl text-[#E7D8B5]">Rates on request</div>
                  </div>
                  <button onClick={() => { setProduct("karwa_chauth"); document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth" }); }}
                    data-testid="cta-karwa-chauth"
                    className="btn-primary small-caps text-[10px] inline-flex items-center gap-2">
                    Book a chef <ArrowRight size={13} />
                  </button>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* ---------- Fusional Feast ---------- */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={POSTER} alt="" aria-hidden className="w-full h-full object-cover opacity-20 blur-sm scale-110" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/85 via-[#0A0A0A]/85 to-[#0A0A0A]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="chip text-[#F5B041] border-[#F5B041]/40"><Sparkles size={12} /> Festival of Lights · Limited evenings</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mt-6">
              Fusional Feast,<br />
              <span className="font-script text-[#F5B041] text-5xl sm:text-6xl lg:text-7xl">a Diwali sit-down.</span>
            </h2>
            <p className="mt-6 text-lg text-neutral-300 max-w-xl leading-relaxed">
              Gourmet Indian cuisine, one extraordinary celebration — a curated, multi-course festive menu
              plated at your table with live counters and a premium sit-down experience.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {["Curated festive menus", "Live counters", "Premium sit-down experience", "Customised for all occasions"].map((t) => (
                <span key={t} className="chip">{t}</span>
              ))}
            </div>
            <div className="mt-8 inline-flex items-end gap-6 border border-white/10 bg-black/50 backdrop-blur px-6 py-4">
              <div>
                <div className="small-caps text-[10px] text-neutral-500">Diwali sit-down dinner</div>
                <div className="font-serif-display text-4xl text-[#F5B041]">from ₹1899</div>
                <div className="small-caps text-[10px] text-neutral-500">per person · T&amp;C applied</div>
              </div>
            </div>
            <div className="mt-8">
              <button onClick={() => { setProduct("diwali_catering"); document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth" }); }}
                className="btn-primary small-caps text-[11px] inline-flex items-center gap-2" data-testid="diwali-cta">
                Book your Diwali celebration <ArrowRight size={14} />
              </button>
            </div>
          </div>
          <div className="justify-self-center">
            <img src={POSTER} alt="Fusional Feast — Diwali gourmet sit-down dinner from ₹1899 per person"
              data-testid="diwali-poster"
              className="w-full max-w-sm border border-[#F5B041]/20 shadow-[0_0_60px_rgba(245,176,65,0.15)]" />
          </div>
        </div>
      </section>

      {/* ---------- Booking ---------- */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-14">
        <div>
          <div className="small-caps text-neutral-500 mb-4">What's included</div>
          <h2 className="font-serif-display text-4xl leading-tight">An evening, taken care of.</h2>
          <ul className="mt-8 space-y-4">
            {[
              "Curated multi-course festive menu, from ₹1899 / person",
              "Live counters and chef + serving team, plated at your home or venue",
              "Premium sit-down experience, vegetarian-forward with Jain, vegan & healthy options",
              "Fully customisable — build your own festive menu with our chefs",
              "Mumbai · Bengaluru · Indore · Delhi NCR · Hyderabad — and on request",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-neutral-300">
                <Check size={16} className="text-[#F5B041] mt-1 shrink-0" /> {t}
              </li>
            ))}
          </ul>
          <p className="text-neutral-500 text-sm mt-8">
            Prefer a full curated menu instead?{" "}
            <Link to="/menus" className="underline underline-offset-4 text-neutral-300 hover:text-white">Browse Mini Catering</Link>.
          </p>
          <p className="text-neutral-500 text-sm mt-3">Call us: +91 79714 13816 · 63525 53515</p>
        </div>

        <div id="enquire" className="bg-[#141414] border border-white/10 p-7">
          <h3 className="font-serif-display text-3xl">Book a festive special</h3>
          <p className="text-neutral-400 text-sm mt-2">Pick what you'd like — our concierge confirms the menu, date and quote.</p>
          <p className="small-caps text-[10px] text-[#F5B041] mt-2" data-testid="last-order-note">
            Gift orders close {LAST_ORDER_LABEL}
          </p>

          <Tabs value={product} onValueChange={(v) => { setProduct(v); setForm((f) => ({ ...f, quantity: "" })); }} className="mt-6">
            <TabsList className="grid grid-cols-3 w-full rounded-none bg-transparent border border-white/10 p-0 h-auto">
              <TabsTrigger value="motichoor_macarons" data-testid="tab-macarons" className="rounded-none text-[10px] uppercase tracking-normal whitespace-normal leading-tight py-3 px-1 data-[state=active]:bg-white/5">Macarons</TabsTrigger>
              <TabsTrigger value="karwa_chauth" data-testid="tab-karwa" className="rounded-none text-[10px] uppercase tracking-normal whitespace-normal leading-tight py-3 px-1 data-[state=active]:bg-white/5">Karwa Chauth</TabsTrigger>
              <TabsTrigger value="diwali_catering" data-testid="tab-catering" className="rounded-none text-[10px] uppercase tracking-normal whitespace-normal leading-tight py-3 px-1 data-[state=active]:bg-white/5">Catering</TabsTrigger>
            </TabsList>
            {Object.keys(PRODUCTS).map((k) => (
              <TabsContent key={k} value={k} className="mt-4">
                <p className="text-xs text-neutral-500">
                  {k === "motichoor_macarons" && "Boxed to order — tell us how many boxes and when you need them."}
                  {k === "karwa_chauth" && "A chef at your home for sargi and the moonrise dinner."}
                  {k === "diwali_catering" && "The Fusional Feast sit-down, from ₹1899 per person."}
                </p>
              </TabsContent>
            ))}
          </Tabs>

          <form onSubmit={submit} className="mt-5 space-y-4">
            {product === "motichoor_macarons" && (
              <div data-testid="hamper-tiers">
                <Label className="small-caps text-[10px] text-neutral-500">Choose your hamper</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {HAMPER_TIERS.map((t) => {
                    const active = Number(form.quantity) === t.boxes;
                    return (
                      <button key={t.boxes} type="button" onClick={() => setForm({ ...form, quantity: String(t.boxes) })}
                        data-testid={`tier-${t.boxes}`}
                        className={`text-left p-3 border transition ${active ? "border-[#C04000] bg-[#C04000]/10" : "border-white/10 hover:border-white/25"}`}>
                        <div className="font-serif-menu text-sm">{t.label}</div>
                        <div className="text-[10px] text-neutral-500 mt-0.5 leading-snug">{t.note}</div>
                        <div className="text-xs text-[#F5B041] mt-1">
                          {t.corporate ? "Rate on request" : `₹${(t.boxes * BOX_PRICE).toLocaleString("en-IN")}`}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {Number(form.quantity) > 0 && (
                  <div className="flex items-center justify-between mt-3 border border-white/10 px-4 py-3" data-testid="hamper-total">
                    <span className="small-caps text-[10px] text-neutral-500">
                      {form.quantity} box{Number(form.quantity) > 1 ? "es" : ""} · {Number(form.quantity) * 6} macarons
                    </span>
                    <span className="font-serif-menu text-lg text-[#F5B041]">
                      {Number(form.quantity) >= 25 ? "Corporate rate on request" : `₹${(Number(form.quantity) * BOX_PRICE).toLocaleString("en-IN")}`}
                    </span>
                  </div>
                )}
              </div>
            )}
            <div>
              <Label className="small-caps text-[10px] text-neutral-500">Your name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="diwali-name"
                className="mt-2 rounded-none bg-transparent border-white/10 h-11" placeholder="Full name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Contact number</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="diwali-phone"
                  className="mt-2 rounded-none bg-transparent border-white/10 h-11" placeholder="+91 98765 43210" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">{cfg.dateLabel}</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="diwali-date"
                  className="mt-2 rounded-none bg-transparent border-white/10 h-11" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">City</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="diwali-city"
                  className="mt-2 rounded-none bg-transparent border-white/10 h-11" placeholder="Mumbai" />
              </div>
              {product === "motichoor_macarons" ? (
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">{cfg.quantityLabel}</Label>
                  <Input type="number" min={1} step={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="diwali-quantity"
                    className="mt-2 rounded-none bg-transparent border-white/10 h-11" placeholder="6" />
                </div>
              ) : (
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">Guests</Label>
                  <Input type="number" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} data-testid="diwali-guests"
                    className="mt-2 rounded-none bg-transparent border-white/10 h-11" placeholder="e.g. 20" />
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <Label className="small-caps text-[10px] text-neutral-500">Anything else?</Label>
                <span className="ml-auto">
                  <DictateButton testId="diwali-dictate"
                    context="A guest describing a festive Diwali or Karwa Chauth order — menus, sweets, guest counts, timings and dietary needs."
                    onText={(t) => setForm((f) => ({ ...f, message: [f.message.trim(), t].filter(Boolean).join(" ") }))} />
                </span>
              </div>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="diwali-message"
                className="mt-2 rounded-none bg-transparent border-white/10 min-h-24"
                placeholder="Menu wishes, gifting notes, delivery address, theme, dietary needs…" />
            </div>
            <Button type="submit" disabled={sending} data-testid="diwali-submit"
              className="w-full rounded-none bg-[#C04000] hover:bg-[#A03500] h-12 small-caps text-[11px]">
              {sending ? "Sending…" : cfg.cta}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
